'use client';

import {
	AnomaliesConfigCard,
	type AnomaliesConfigCardData,
	AnomaliesConfigCardSkeleton,
	AnomaliesView,
} from '@slims/ui/organisms';
import { StatusBlock } from '@slims/ui/molecules';
import { useState } from 'react';
import { toast } from 'sonner';
import {
	MetricExplode,
	type MetricExplodeTarget,
	PodDetailExplode,
	type PodDetailTarget,
} from '@/components/energy/metric-explode';
import {
	type AnomalyStatusFilter,
	useConsumptionAnomalies,
} from '@/hooks/use-consumption-anomalies';
import { usePlantAnomalies, usePlantFaultTypes } from '@/hooks/use-plant-anomalies';
import { useRegistryAnomalies } from '@/hooks/use-registry-anomalies';
import { usePlantDetail } from '@/hooks/use-plant-detail';
import type { ScopeLeaf } from '@/hooks/use-scope-tree';
import { useScopedConfig } from '@/hooks/use-scoped-config';
import { type ConfigToggleProps, useConfigToggle } from '@/hooks/use-config-toggle';

/**
 * Come Dashboard, Monitoraggio, Previsione e Incentivi, la pagina Anomalie
 * guarda le configurazioni scelte nel selettore CER/configurazioni dell'header.
 * Con più di una in ambito compare il toggle «Panoramica»/«Configurazione
 * singola»: la Panoramica è una card per configurazione col conteggio delle
 * anomalie aperte, il clic apre il pannello completo di quella.
 */
export default function AnomaliesPage() {
	const { scopes, loading: scopesLoading, effectiveLeaf, openConfig, toggle, showOverview } =
		useConfigToggle('anomalies');

	if (!effectiveLeaf) {
		return (
			<StatusBlock
				kind={scopesLoading ? 'loading' : 'empty'}
				size="lg"
				message={
					scopesLoading
						? 'Caricamento...'
						: 'Nessuna configurazione selezionata. Scegli una vista dal selettore in alto.'
				}
			/>
		);
	}

	if (showOverview) {
		return (
			<AnomaliesView
				{...toggle}
				overviewGridSlot={scopes.map((leaf) => (
					<AnomaliesConfigCardWiring
						key={leaf.orgId}
						leaf={leaf}
						onOpen={() => openConfig(leaf.orgId)}
					/>
				))}
			/>
		);
	}

	return <AnomaliesSingleWiring key={effectiveLeaf.orgId} leaf={effectiveLeaf} toggle={toggle} />;
}

/** Alimenta una card della «Panoramica»: le anomalie aperte di una configurazione sola. */
function AnomaliesConfigCardWiring({ leaf, onOpen }: { leaf: ScopeLeaf; onOpen: () => void }) {
	const { config } = useScopedConfig(leaf.orgId);
	const { counts, isLoading: plantLoading } = usePlantAnomalies(leaf.orgId, {
		status: 'open',
		plantId: null,
	});
	const { anomalies: consumi, isLoading: consumiLoading } = useConsumptionAnomalies(
		leaf.orgId,
		'open',
	);
	const { anomalies: anagrafica } = useRegistryAnomalies(leaf.orgId);

	if (plantLoading || consumiLoading) {
		return <AnomaliesConfigCardSkeleton cerNome={leaf.cerNome} />;
	}

	const data: AnomaliesConfigCardData = {
		id: leaf.orgId,
		cerNome: leaf.cerNome,
		configNome: config.nome || leaf.configNome,
		comune: leaf.comune,
		areaCode: leaf.areaConvenzionale,
		crit: counts.crit,
		warn: counts.warn,
		consumiAperte: consumi.length,
		risolteOggi: counts.risolteOggi,
		anagraficaErrori: anagrafica.filter((a) => a.livello === 'errore').length,
		anagraficaAvvisi: anagrafica.filter((a) => a.livello === 'avviso').length,
	};

	return <AnomaliesConfigCard data={data} onClick={onOpen} />;
}

/**
 * Il pannello Anomalie completo di una configurazione sola: puro cablaggio dati.
 * La `key` sull'orgId nel chiamante rimonta questo blocco quando si cambia
 * configurazione dal picker, azzerando l'impianto e il filtro selezionati.
 */
function AnomaliesSingleWiring({ leaf, toggle }: { leaf: ScopeLeaf; toggle: ConfigToggleProps }) {
	const orgId = leaf.orgId;

	const [plantId, setPlantId] = useState<string | null>(null);
	const [status, setStatus] = useState<AnomalyStatusFilter>('all');
	const [savingId, setSavingId] = useState<string | null>(null);

	const plant = usePlantAnomalies(orgId, { status: 'open', plantId });
	const { faultTypes } = usePlantFaultTypes(orgId);
	// Solo per l'elenco del selettore: la scheda impianto non serve qui.
	const { options } = usePlantDetail(orgId, null);
	const consumption = useConsumptionAnomalies(orgId, status);
	const registry = useRegistryAnomalies(orgId);

	const [explode, setExplode] = useState<PodDetailTarget | null>(null);
	const [metric, setMetric] = useState<MetricExplodeTarget | null>(null);

	async function resolve(kind: 'plant' | 'consumption', id: string, resolved: boolean) {
		setSavingId(id);
		const ok =
			kind === 'plant'
				? await plant.setResolved(id, resolved)
				: await consumption.setResolved(id, resolved);
		setSavingId(null);
		if (ok) toast.success(resolved ? 'Anomalia presa in carico' : 'Anomalia riaperta');
		else toast.error('Errore nel salvataggio');
	}

	return (
		<>
			<AnomaliesView
				{...toggle}
				plantAnomalies={plant.anomalies}
				plantCounts={plant.counts}
				plantOptions={options}
				selectedPlantId={plantId}
				onSelectPlant={setPlantId}
				faultTypesCount={faultTypes.length || undefined}
				onResolvePlantAnomaly={(id, resolved) => resolve('plant', id, resolved)}
				plantSavingId={savingId}
				plantLoading={plant.isLoading}
				plantError={plant.error}
				consumptionAnomalies={consumption.anomalies}
				consumptionStatus={status}
				onConsumptionStatusChange={setStatus}
				onResolveConsumptionAnomaly={(id, resolved) => resolve('consumption', id, resolved)}
				consumptionSavingId={savingId}
				consumptionLoading={consumption.isLoading}
				consumptionError={consumption.error}
				registryAnomalies={registry.anomalies}
				registryLoading={registry.isLoading}
				registryError={registry.error}
				onExplodeMetric={(t) => setMetric(t as MetricExplodeTarget)}
				onExplodePodDay={setExplode}
			/>
			<PodDetailExplode target={explode} onClose={() => setExplode(null)} />
			<MetricExplode target={metric} onClose={() => setMetric(null)} />
		</>
	);
}
